import { useEffect, useRef, useState } from "react";
import { Modal, Dropdown } from "react-bootstrap";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  registerPartnerUser,
  updateUserGroup,
} from "../../../services/PartnerService";
import { formatKenyanPhoneNumber } from "../../../utils/helpers";
import { showMutationError } from "../../../utils/showMutationError";
import {
  isExpenseTagPrerequisiteError,
  isStaleTeamSelectionError,
  prerequisiteMessageFor,
} from "../../../utils/prerequisiteErrors";
import PrerequisiteAlert from "../PrerequisiteAlert";
import { usePartnerHasSoloUser } from "../../../hooks/usePartnerHasSoloUser";
import TeamRevealStep, {
  initialTeamRevealState,
  isTeamRevealValid,
  resolveTeamIdFromReveal,
} from "./TeamRevealStep";

const emptyForm = {
  first_name: "",
  last_name: "",
  preferred_name: "",
  email: "",
  phone: "",
  role_id: "",
  with_minipot: false,
  team_id: "",
};

const AddUserModal = ({
  show,
  onHide,
  partnerId,
  roles,
  rolesLoading,
  allPartnerTeams,
  hasExpenseTags,
  expenseTagsKnown = true,
  teamsKnown = true,
  onAddExpenseTag,
  onAddTeam,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [topLevelError, setTopLevelError] = useState(null);
  const [revealState, setRevealStateRaw] = useState(initialTeamRevealState);
  const [processing, setProcessing] = useState(false);
  const [createdNewTeamId, setCreatedNewTeamId] = useState(null);
  const [createdNewTeamName, setCreatedNewTeamName] = useState(null);

  const setRevealState = (nextOrUpdater) => {
    setRevealStateRaw((prev) => {
      const next = typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;
      if (next.choice !== prev.choice) {
        setCreatedNewTeamId(null);
        setCreatedNewTeamName(null);
      }
      return next;
    });
  };
  const modalBodyRef = useRef(null);
  const queryClient = useQueryClient();

  const { hasSoloUser, hasSoloWalletHolder } = usePartnerHasSoloUser(partnerId);
  const defaultTeam = allPartnerTeams?.find((team) => team.is_default);
  const shouldShowRevealStep = hasSoloWalletHolder && formData.with_minipot;
  const selectedRoleGrantsLimitEditing = (() => {
    const role = roles?.find((r) => String(r.id) === String(formData.role_id));
    return role?.name === "Super Admin";
  })();

  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;
    const firstInvalid = modalBodyRef.current?.querySelector(".is-invalid");
    firstInvalid?.focus?.();
  }, [fieldErrors]);

  useEffect(() => {
    if (!hasSoloUser) return;
    if (shouldShowRevealStep) return;
    if (!formData.with_minipot) return;
    if (!defaultTeam) return;
    if (String(formData.team_id) === String(defaultTeam.id)) return;
    setFormData((prev) => ({ ...prev, team_id: defaultTeam.id }));
  }, [hasSoloUser, shouldShowRevealStep, formData.with_minipot, formData.team_id, defaultTeam]);

  const resetForm = () => {
    setFormData(emptyForm);
    setFieldErrors({});
    setTopLevelError(null);
    setRevealStateRaw(initialTeamRevealState);
    setCreatedNewTeamId(null);
    setCreatedNewTeamName(null);
    setProcessing(false);
  };

  const close = () => {
    resetForm();
    onHide();
  };

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    let parsedValue;
    if (id === "with_minipot") {
      parsedValue = value === "true";
    } else if (id === "phone") {
      parsedValue = formatKenyanPhoneNumber(value);
    } else {
      parsedValue = value;
    }
    setFormData((prev) => ({ ...prev, [id]: parsedValue }));
    clearFieldError(id);
  };

  const mutation = useMutation({
    mutationFn: (data) => registerPartnerUser(partnerId, data),
  });

  const hasUserGroups = allPartnerTeams?.length > 0;
  const missingExpenseTag = expenseTagsKnown && !hasExpenseTags;
  const missingTeam = teamsKnown && !hasUserGroups;
  const walletPrerequisitesReady = expenseTagsKnown && teamsKnown && hasExpenseTags && hasUserGroups;

  const revealStepIsValid = !shouldShowRevealStep || isTeamRevealValid(revealState);

  const formIsValid =
    formData.first_name.trim() &&
    formData.last_name.trim() &&
    formData.email.trim() &&
    formData.role_id &&
    (formData.with_minipot
      ? (shouldShowRevealStep
          ? walletPrerequisitesReady
          : formData.team_id && walletPrerequisitesReady)
      : true) &&
    revealStepIsValid;

  const resolveTeamForInvite = async () => {
    if (!shouldShowRevealStep) {
      return formData.team_id;
    }

    if (revealState.choice === "new" && createdNewTeamId) {
      const trimmed = revealState.teamName.trim();
      if (trimmed && trimmed !== createdNewTeamName) {
        await updateUserGroup(partnerId, createdNewTeamId, { name: trimmed });
        queryClient.invalidateQueries({ queryKey: ["allPartnerTeams", partnerId] });
        setCreatedNewTeamName(trimmed);
      }
      return createdNewTeamId;
    }

    const resolvedId = await resolveTeamIdFromReveal({
      state: revealState,
      partnerId,
      defaultTeam,
      queryClient,
    });

    if (revealState.choice === "new" && resolvedId) {
      setCreatedNewTeamId(resolvedId);
      setCreatedNewTeamName(revealState.teamName.trim());
    }

    return resolvedId ?? formData.team_id;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setTopLevelError(null);
    setProcessing(true);
    try {
      const resolvedTeamId = await resolveTeamForInvite();
      if (formData.with_minipot && !resolvedTeamId) {
        setTopLevelError("Could not prepare the team assignment. Please try again in a moment.");
        return;
      }
      const payload = { ...formData, team_id: resolvedTeamId };
      const res = await mutation.mutateAsync(payload);
      queryClient.invalidateQueries({ queryKey: ["activePartnerUsersCount", partnerId] });
      queryClient.invalidateQueries({ queryKey: ["partnerHasSoloUser", partnerId] });
      toast.success(res?.data?.message || "User added successfully!", { autoClose: 10000 });
      onSuccess?.();
      close();
    } catch (err) {
      if (err?.response?.status === 422) {
        queryClient.invalidateQueries({ queryKey: ["expenseTags", partnerId] });
        queryClient.invalidateQueries({ queryKey: ["allPartnerTeams", partnerId] });
      }
      if (isExpenseTagPrerequisiteError(err)) {
        setTopLevelError(prerequisiteMessageFor(err, "Add at least one expense tag before assigning a wallet."));
        return;
      }
      if (isStaleTeamSelectionError(err)) {
        setTopLevelError("The team you selected is no longer available. Please pick another.");
        setFormData((prev) => ({ ...prev, team_id: "" }));
        return;
      }
      showMutationError(err, {
        fallback: "Failed to add user.",
        onFieldErrors: setFieldErrors,
      });
    } finally {
      setProcessing(false);
    }
  };

  const selectedRole = roles?.find((role) => String(role.id) === String(formData.role_id));
  const currentRoleLabel = selectedRole
    ? selectedRole.name
    : (rolesLoading ? "Loading roles..." : "Select a role...");
  const walletLabel = formData.with_minipot ? "Yes" : "No";
  const selectedTeam = allPartnerTeams?.find((team) => String(team.id) === String(formData.team_id));
  const currentTeamLabel = selectedTeam ? selectedTeam.name : "Select a team...";

  const inputClassName = (fieldKey) =>
    `form-control input-default${fieldErrors[fieldKey] ? " is-invalid" : ""}`;

  const renderFieldError = (fieldKey) =>
    fieldErrors[fieldKey] ? (
      <div className="invalid-feedback d-block">{fieldErrors[fieldKey]}</div>
    ) : null;

  return (
    <Modal show={show} onHide={close} className="modal fade" data-tour="users-add-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Add User</h5>
          <button type="button" onClick={close} className="btn-close" data-dismiss="modal" />
        </div>
        <div className="modal-body" ref={modalBodyRef}>
          <form onSubmit={handleSubmit} noValidate>
            <div data-tour="users-add-name-fields">
              <label className="form-check-label" htmlFor="first_name">First Name</label>
              <div className="form-group mb-3">
                <input type="text" className={inputClassName("first_name")} id="first_name" value={formData.first_name} onChange={handleInputChange} />
                {renderFieldError("first_name")}
              </div>
              <label className="form-check-label" htmlFor="last_name">Last Name</label>
              <div className="form-group mb-3">
                <input type="text" className={inputClassName("last_name")} id="last_name" value={formData.last_name} onChange={handleInputChange} />
                {renderFieldError("last_name")}
              </div>
              <label className="form-check-label" htmlFor="preferred_name">Preferred Name</label>
              <div className="form-group mb-3">
                <input type="text" className={inputClassName("preferred_name")} id="preferred_name" value={formData.preferred_name} onChange={handleInputChange} />
                {renderFieldError("preferred_name")}
              </div>
            </div>
            <div data-tour="users-add-contact-fields">
              <label className="form-check-label" htmlFor="email">Email</label>
              <div className="form-group mb-3">
                <input type="email" className={inputClassName("email")} id="email" value={formData.email} onChange={handleInputChange} />
                {renderFieldError("email")}
              </div>
              <label className="form-check-label" htmlFor="phone">Phone Number <span className="text-muted fw-normal">(optional)</span></label>
              <div className="form-group mb-3">
                <input type="text" className={inputClassName("phone")} id="phone" value={formData.phone} onChange={handleInputChange} />
                {renderFieldError("phone")}
              </div>
            </div>
            <div className="mb-3" data-tour="users-add-role">
              <label className="form-label d-block">Select role</label>
              <Dropdown className="w-100">
                <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center w-100" disabled={rolesLoading}>
                  <span className="flex-grow-1 text-start">{currentRoleLabel}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {roles?.map((role) => (
                    <Dropdown.Item
                      key={role.id}
                      active={String(formData.role_id) === String(role.id)}
                      onClick={() => handleInputChange({ target: { id: "role_id", value: role.id } })}
                    >
                      {role.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              {renderFieldError("role_id")}
            </div>
            <div className="mb-3" data-tour="users-add-wallet-toggle">
              <label className="form-label d-block">Create user wallet</label>
              <Dropdown className="w-100">
                <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center w-100">
                  <span className="flex-grow-1 text-start">{walletLabel}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item active={formData.with_minipot === true} onClick={() => handleInputChange({ target: { id: "with_minipot", value: "true" } })}>
                    Yes
                  </Dropdown.Item>
                  <Dropdown.Item active={formData.with_minipot === false} onClick={() => handleInputChange({ target: { id: "with_minipot", value: "false" } })}>
                    No
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {renderFieldError("with_minipot")}
            </div>
            {formData.with_minipot && (
              <>
                {missingExpenseTag && (
                  <PrerequisiteAlert
                    message="At least one expense tag must exist before assigning a wallet."
                    actionLabel="Add expense tag"
                    onAction={onAddExpenseTag}
                  />
                )}
                {!hasSoloUser && missingTeam && (
                  <PrerequisiteAlert
                    message="At least one team must exist before assigning a wallet."
                    actionLabel="Add team"
                    onAction={onAddTeam}
                  />
                )}
                {shouldShowRevealStep && (
                  <TeamRevealStep
                    firstName={formData.first_name}
                    defaultTeam={defaultTeam}
                    roleGrantsLimitEditing={selectedRoleGrantsLimitEditing}
                    state={revealState}
                    onStateChange={setRevealState}
                  />
                )}
                {!hasSoloUser && !shouldShowRevealStep && (
                  <div className="mb-3" data-tour="users-add-team">
                    <label className="form-label d-block">Select team</label>
                    <Dropdown className="w-100">
                      <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center w-100">
                        <span className="flex-grow-1 text-start">{currentTeamLabel}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {allPartnerTeams?.map((team) => (
                          <Dropdown.Item
                            key={team.id}
                            active={String(formData.team_id) === String(team.id)}
                            onClick={() => handleInputChange({ target: { id: "team_id", value: team.id } })}
                          >
                            {team.name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    {renderFieldError("team_id")}
                  </div>
                )}
              </>
            )}
            {topLevelError && (
              <PrerequisiteAlert message={topLevelError} />
            )}
            <button className="btn btn-primary btn-block" disabled={!formIsValid || processing || mutation.isPending} data-tour="users-add-submit">
              {(processing || mutation.isPending) ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                "Add"
              )}
            </button>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddUserModal;
